/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import type { ClientMappingStatus } from "../../API/ClientApi";

interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  label: string; // shown as modal title (clientId or documentId)
  imageUrl: string; // BASE_URL + "/api/media/file/" + documentId
  status: ClientMappingStatus;
  onApprove: () => void;
  onReject: () => void;
  clientId: string;
  onClientIdChange: (value: string) => void;
  onUpdateClientId: () => void;
  actionLoading: boolean;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  onClose,
  label,
  imageUrl,
  status,
  onApprove,
  onReject,
  clientId,
  onClientIdChange,
  onUpdateClientId,
  actionLoading,
}) => {
  const token = localStorage.getItem("access_token");

  const objectUrlRef = useRef<string | null>(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const [lastAction, setLastAction] = useState<
    "APPROVE" | "REJECT" | null
  >(null);
const isApproved = status === "APPROVED";
  useEffect(() => {
    if (!open) return;

    setImgLoading(true);
    setImgError(false);
    setObjectUrl(null);

    const currentObjectUrl = objectUrlRef.current;
    if (currentObjectUrl) {
      URL.revokeObjectURL(currentObjectUrl);
      objectUrlRef.current = null;
    }

    let cancelled = false;

    const run = async () => {
      try {
        if (!imageUrl) throw new Error("Missing imageUrl");

        const response = await fetch(imageUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image (${response.status})`);
        }

        const blob = await response.blob();
        const nextObjectUrl = URL.createObjectURL(blob);

        if (cancelled) {
          URL.revokeObjectURL(nextObjectUrl);
          return;
        }

        objectUrlRef.current = nextObjectUrl;
        setObjectUrl(nextObjectUrl);
      } catch (error: unknown) {
        if (!cancelled) {
          console.error("Error fetching image preview:", error);
        
          setImgError(true);
          setObjectUrl(null);
        }
      } finally {
        if (!cancelled) setImgLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [imageUrl, open, token]);

  const isPending = status === "PENDING";

  const badge = (() => {
    if (status === "APPROVED") return { label: "Approved", bg: "#00CD5B" };
    if (status === "REJECTED") return { label: "Rejected", bg: "#FF6347" };
    return { label: "Pending", bg: "#FFA500" };
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <Box sx={{ px: 3, pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            pb: 1.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 900, color: "#455A64", lineHeight: 1.1 }}
          >
            {label}
          </Typography>
          <IconButton
            onClick={onClose}
            disabled={actionLoading}
            sx={{ color: "#9e9e9e" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box className="image-preview-area">
            {imgLoading ? (
              <CircularProgress sx={{ color: "#00CD5B" }} />
            ) : imgError || !objectUrl ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ImageNotSupportedOutlinedIcon
                  sx={{ fontSize: 64, color: "#A4A2A2" }}
                />
                <Typography sx={{ color: "#A4A2A2", fontWeight: 700 }}>
                  Image Preview
                </Typography>
              </Box>
            ) : (
              <img
                src={objectUrl}
                alt="preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", sm: "flex-end" },
              gap: 1.5,
              mt: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#455A64",
                  mb: 0.6,
                }}
              >
                Client ID
              </Typography>

             <TextField
  fullWidth
  size="small"
  value={clientId}
  onChange={(e) => {
    if (!isApproved) {
      onClientIdChange(e.target.value);
    }
  }}
  inputProps={{ maxLength: 120 }}
  InputProps={{
    readOnly: isApproved,
  }}
  sx={{
    backgroundColor: isApproved ? "#F5F5F5" : "#fff",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      height: 50,
      "& fieldset": {
        borderColor: "#D9DDE3",
      },
      "&:hover fieldset": {
        borderColor: "#C8D0D8",
      },
      "&.Mui-focused fieldset": {
        borderColor: isApproved ? "#D9DDE3" : "#00CD5B",
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      py: 1.4,
      cursor: isApproved ? "not-allowed" : "text",
    },
  }}
/>
            </Box>

           <Button
  variant="contained"
  disabled={
    actionLoading ||
    clientId.trim().length === 0 ||
    status?.toUpperCase() === "APPROVED"
  }
  onClick={onUpdateClientId}
  sx={{
    background: "#00CD5B",
    color: "#fff",
    borderRadius: "8px",
    px: 3,
    py: 1,
    fontWeight: 700,
    textTransform: "none",
    height: 50,
    minWidth: 120,
    whiteSpace: "nowrap",
    "&:hover": { background: "#00b34e" },
    "&.Mui-disabled": {
      background: "#BDBDBD",
      color: "#fff",
      cursor: "not-allowed",
    },
  }}
>
  {actionLoading ? (
    <CircularProgress size={20} sx={{ color: "#fff" }} />
  ) : (
    "Update"
  )}
</Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              mt: 2,
            }}
          >
            {isPending ? (
              <>
                <Button
                  variant="contained"
                  disabled={actionLoading}
                  onClick={() => {
                    setLastAction("APPROVE");
                    onApprove();
                    isApproved
                  }}
                  sx={{
                    background: "#00CD5B",
                    color: "#fff",
                    borderRadius: "999px",
                    px: 3,
                    py: 1,
                    fontWeight: 700,
                    "&:hover": { background: "#00b34e" },
                  }}
                >
                  {actionLoading && lastAction === "APPROVE" ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Approve"
                  )}
                </Button>

                <Button
                  variant="contained"
                  disabled={actionLoading}
                  onClick={() => {
                    setLastAction("REJECT");
                    onReject();
                  }}
                  sx={{
                    background: "#FF6347",
                    color: "#fff",
                    borderRadius: "999px",
                    px: 3,
                    py: 1,
                    fontWeight: 700,
                    "&:hover": { background: "#e0523a" },
                  }}
                >
                  {actionLoading && lastAction === "REJECT" ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Reject"
                  )}
                </Button>
              </>
            ) : (
              <Box
                sx={{
                  background: badge.bg,
                  color: "#fff",
                  borderRadius: "999px",
                  px: 3,
                  py: 1,
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {badge.label}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ImagePreviewModal;

/*
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import type { ClientMappingStatus } from "../../API/ClientApi";

interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  label: string; // shown as modal title (clientId or documentId)
  imageUrl: string; // BASE_URL + "/api/media/file/" + documentId
  status: ClientMappingStatus;
  onApprove: () => void;
  onReject: () => void;
  actionLoading: boolean;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  onClose,
  label,
  imageUrl,
  status,
  onApprove,
  onReject,
  actionLoading,
}) => {
  const token = localStorage.getItem("access_token");

  const objectUrlRef = useRef<string | null>(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const [lastAction, setLastAction] = useState<
    "APPROVE" | "REJECT" | null
  >(null);

  useEffect(() => {
    if (!open) return;

    // Reset state for new previews
    setImgLoading(true);
    setImgError(false);
    setObjectUrl(null);

    const currentObjectUrl = objectUrlRef.current;
    if (currentObjectUrl) {
      URL.revokeObjectURL(currentObjectUrl);
      objectUrlRef.current = null;
    }

    let cancelled = false;

    const run = async () => {
      try {
        if (!imageUrl) {
          throw new Error("Missing imageUrl");
        }

        const response = await fetch(imageUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image (${response.status})`);
        }

        const blob = await response.blob();
        const nextObjectUrl = URL.createObjectURL(blob);

        if (cancelled) {
          URL.revokeObjectURL(nextObjectUrl);
          return;
        }

        objectUrlRef.current = nextObjectUrl;
        setObjectUrl(nextObjectUrl);
      } catch (error: unknown) {
        if (!cancelled) {
          console.error("Error fetching image preview:", error);
   
          setImgError(true);
          setObjectUrl(null);
        }
      } finally {
        if (!cancelled) {
          setImgLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [imageUrl, open, token]);

  const isPending = status === "PENDING";

  const handleApproveClick = () => {
    setLastAction("APPROVE");
    onApprove();
  };

  const handleRejectClick = () => {
    setLastAction("REJECT");
    onReject();
  };

  const badge = (() => {
    if (status === "APPROVED") {
      return { label: "Approved", bg: "#00CD5B" };
    }
    if (status === "REJECTED") {
      return { label: "Rejected", bg: "#FF6347" };
    }
    return { label: "Pending", bg: "#FFA500" };
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <Box sx={{ px: 3, pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            pb: 1.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 900, color: "#455A64", lineHeight: 1.1 }}
          >
            {label}
          </Typography>
          <IconButton
            onClick={onClose}
            disabled={actionLoading}
            sx={{ color: "#9e9e9e" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box className="image-preview-area">
            {imgLoading ? (
              <CircularProgress sx={{ color: "#00CD5B" }} />
            ) : imgError || !objectUrl ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ImageNotSupportedOutlinedIcon
                  sx={{ fontSize: 64, color: "#A4A2A2" }}
                />
                <Typography sx={{ color: "#A4A2A2", fontWeight: 700 }}>
                  Image Preview
                </Typography>
              </Box>
            ) : (
              <img
                src={objectUrl}
                alt="preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              mt: 2,
            }}
          >
            {isPending ? (
              <>
                <Button
                  variant="contained"
                  onClick={handleApproveClick}
                  disabled={actionLoading}
                  sx={{
                    background: "#00CD5B",
                    color: "#fff",
                    borderRadius: "999px",
                    px: 3,
                    py: 1,
                    fontWeight: 700,
                    "&:hover": { background: "#00b34e" },
                  }}
                >
                  {actionLoading && lastAction === "APPROVE" ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Approve"
                  )}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleRejectClick}
                  disabled={actionLoading}
                  sx={{
                    background: "#FF6347",
                    color: "#fff",
                    borderRadius: "999px",
                    px: 3,
                    py: 1,
                    fontWeight: 700,
                    "&:hover": { background: "#e0523a" },
                  }}
                >
                  {actionLoading && lastAction === "REJECT" ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Reject"
                  )}
                </Button>
              </>
            ) : (
              <Box
                sx={{
                  background: badge.bg,
                  color: "#fff",
                  borderRadius: "999px",
                  px: 3,
                  py: 1,
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {badge.label}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ImagePreviewModal;

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import type { ClientMappingStatus } from "../../API/ClientApi";

interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  label: string; // shown as modal title (clientId or documentId)
  imageUrl: string; // BASE_URL + "/api/media/file/" + documentId
  status: ClientMappingStatus;
  onApprove: () => void;
  onReject: () => void;
  actionLoading: boolean;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  onClose,
  label,
  imageUrl,
  status,
  onApprove,
  onReject,
  actionLoading,
}) => {
  const token = localStorage.getItem("access_token");

  const objectUrlRef = useRef<string | null>(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const [lastAction, setLastAction] = useState<"APPROVE" | "REJECT" | null>(
    null
  );

  useEffect(() => {
    if (!open) return;

    // Reset state for new previews
    setImgLoading(true);
    setImgError(false);
    setObjectUrl(null);

    const currentObjectUrl = objectUrlRef.current;
    if (currentObjectUrl) {
      URL.revokeObjectURL(currentObjectUrl);
      objectUrlRef.current = null;
    }

    let cancelled = false;

    const run = async () => {
      try {
        if (!imageUrl) {
          throw new Error("Missing imageUrl");
        }

        const response = await fetch(imageUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image (${response.status})`);
        }

        const blob = await response.blob();
        const nextObjectUrl = URL.createObjectURL(blob);

        if (cancelled) {
          URL.revokeObjectURL(nextObjectUrl);
          return;
        }

        objectUrlRef.current = nextObjectUrl;
        setObjectUrl(nextObjectUrl);
      } catch (error: unknown) {
        if (!cancelled) {
          console.error("Error fetching image preview:", error);
          alert("Failed to load image preview.");
          setImgError(true);
          setObjectUrl(null);
        }
      } finally {
        if (!cancelled) {
          setImgLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [imageUrl, open, token]);

  const isPending = status === "PENDING";

  const handleApproveClick = () => {
    setLastAction("APPROVE");
    onApprove();
  };

  const handleRejectClick = () => {
    setLastAction("REJECT");
    onReject();
  };

  const badge = (() => {
    if (status === "APPROVED") {
      return { label: "Approved", bg: "#00CD5B" };
    }
    if (status === "REJECTED") {
      return { label: "Rejected", bg: "#FF6347" };
    }
    return { label: "Pending", bg: "#FFA500" };
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <Box sx={{ px: 3, pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            pb: 1.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 900, color: "#455A64", lineHeight: 1.1 }}
          >
            {label}
          </Typography>
          <IconButton onClick={onClose} disabled={actionLoading} sx={{ color: "#9e9e9e" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box className="image-preview-area">
            {imgLoading ? (
              <CircularProgress sx={{ color: "#00CD5B" }} />
            ) : imgError || !objectUrl ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <ImageNotSupportedOutlinedIcon sx={{ fontSize: 64, color: "#A4A2A2" }} />
                <Typography sx={{ color: "#A4A2A2", fontWeight: 700 }}>
                  Image Preview
                </Typography>
              </Box>
            ) : (
              <img
                src={objectUrl}
                alt="preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              mt: 2,
            }}
          >
            {isPending ? (
              <>
                <Button
                  variant="contained"
                  onClick={handleApproveClick}
                  disabled={actionLoading}
                  sx={{
                    background: "#00CD5B",
                    color: "#fff",
                    borderRadius: "999px",
                    px: 3,
                    py: 1,
                    fontWeight: 700,
                    "&:hover": { background: "#00b34e" },
                  }}
                >
                  {actionLoading && lastAction === "APPROVE" ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Approve"
                  )}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleRejectClick}
                  disabled={actionLoading}
                  sx={{
                    background: "#FF6347",
                    color: "#fff",
                    borderRadius: "999px",
                    px: 3,
                    py: 1,
                    fontWeight: 700,
                    "&:hover": { background: "#e0523a" },
                  }}
                >
                  {actionLoading && lastAction === "REJECT" ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Reject"
                  )}
                </Button>
              </>
            ) : (
              <Box
                sx={{
                  background: badge.bg,
                  color: "#fff",
                  borderRadius: "999px",
                  px: 3,
                  py: 1,
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {badge.label}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ImagePreviewModal;

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { ClientMappingStatus } from "../../API/ClientApi";

export interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  label: string;
  imageUrl: string;
  status: ClientMappingStatus;
  onApprove: () => void | Promise<void>;
  onReject: () => void | Promise<void>;
  actionLoading?: boolean;
}

const getStatusMeta = (status: ClientMappingStatus) => {
  switch (status) {
    case "PENDING":
      return { label: "Pending", color: "#FFA500" };
    case "APPROVED":
      return { label: "Approved", color: "#00CD5B" };
    case "REJECTED":
      return { label: "Rejected", color: "#FF4444" };
    default:
      return { label: status, color: "#455A64" };
  }
};

export default function ImagePreviewModal({
  open,
  onClose,
  label,
  imageUrl,
  status,
  onApprove,
  onReject,
  actionLoading = false,
}: ImagePreviewModalProps) {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (open) setImageLoaded(false);
  }, [open, imageUrl]);

  const statusMeta = getStatusMeta(status);
  const showActions = status === "PENDING";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 2,
          pb: 1,
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#455A64" }}>
            Image Name
          </Typography>
          <Typography sx={{ fontWeight: 600, color: "#000" }}>
            {label}
          </Typography>
        </Box>

        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
        <Box className="manage-b2c-image-preview-area" sx={{ width: "100%" }}>
          {!imageLoaded && (
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress sx={{ color: "#00CD5B" }} />
              <Typography sx={{ mt: 1, color: "#6B7280" }}>
                Loading...
              </Typography>
            </Box>
          )}
          {/* Keep the element mounted so layout doesn't jump * /}
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <img
              src={imageUrl}
              alt="Preview"
              onLoad={() => setImageLoaded(true)}
              style={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                display: imageLoaded ? "block" : "none",
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            alignItems: "center",
          }}
        >
          {showActions ? (
            <>
              <Button
                variant="contained"
                onClick={onApprove}
                disabled={actionLoading}
                sx={{
                  backgroundColor: "#00CD5B",
                  borderRadius: "999px",
                  fontWeight: 800,
                  px: 3,
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#00CD5B" },
                }}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                onClick={onReject}
                disabled={actionLoading}
                sx={{
                  backgroundColor: "#FF6347",
                  borderRadius: "999px",
                  fontWeight: 800,
                  px: 3,
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#FF6347" },
                }}
              >
                Reject
              </Button>
            </>
          ) : (
            <Box
              className="manage-b2c-status-badge"
              sx={{
                backgroundColor: "rgba(0,0,0,0.02)",
                border: `1px solid ${statusMeta.color}33`,
                color: statusMeta.color,
              }}
            >
              {statusMeta.label}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

*/